import FormData from 'form-data';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../components/ui/Button';
import FileUploadAudio from '../../components/ui/FileUploadAudio';
import Loader from '../../components/ui/Loader';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../../components/ui/Modal';
import { PatientInfo, useApiReload } from '../../context/apiRelaod';
import { useAudioRecording, RecordingStatus } from '../../context/AudioRecordingContext';
import { useAuth } from '../../context/authContext';
import { CaseAdd, PatientFetch } from '../../services/apiService';
import useAddNote from '../../services/audioNote';
import { dateFormat } from '../../utils/priceFormat.util';
import AudioRecorderComponent from '../AudioRecorder';
import SimpleLoader from '../../components/ui/SimpleLoader';

export type IcaseProps = {
	patientId?: string;
	caseName?: string;
	caseId?: string | null;
	audioUrl?: FormData;
	noteType?: string;
};

const NewNoteCase = () => {
	const navigate = useNavigate();

	const { type } = useParams();
	const { state } = useLocation();
	const [isModalOpen, setIsModalOpen] = useState(false);
	
	const {
		patientID,
		setPatientID,
		caseInfo,
		setcaseInfo,
		setReloadPatient,
		isRecording,
		isOnNewNoteScreen,
		setIsOnNewNoteScreen,
	} = useApiReload();
	
	// Use the dictionary-based audio recording context
	const { recordings, pauseRecording, deleteRecording } = useAudioRecording();
	
	const { onLogout } = useAuth();
	const { NOTE } = state || {};
	const { call } = CaseAdd();
	const { addNote } = useAddNote();
	const { Patient_call, Patient_loading, Patient_response } = PatientFetch();
	const [patient, setPatient] = useState<PatientInfo | null>(null);
	const [caseData, setCaseData] = useState<any>();

	const [file, setFile] = useState<File>();

	const [selectedNote, setSelectedNote] = useState<string>('INITIAL_NOTE');
	
	const handleOpenModal = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	
	useEffect(() => {
		if (type === 'Anonymous') {
			setPatientID({
				firstName: 'Anonymous',
				lastName: '',
				email: 'N/A',
				id: 'Anonymous',
				moblieNo: 'N/A',
			});
			setcaseInfo(null);
		}
	}, [type]);

	useEffect(() => {
		return () => {
			setIsOnNewNoteScreen(false);
		};
	}, []);

	useEffect(() => {
		if (NOTE === 'Followup') {
			setSelectedNote('FOLLOWUP_NOTE');
		} else {
			setSelectedNote('INITIAL_NOTE');
		}
	}, [type]);

	useEffect(() => {
		if (!file) return;
		setIsSubmitting(true);	
		addNote({
			patientId: patientID?.id,
			caseId: caseInfo?.id,
			noteType: selectedNote,
			file,
		}).subscribe({
			next: (res: any) => {
				toast.success(res?.msg);
				setReloadPatient(true);
				setFile(undefined);
			},
			error: (e: any) => {
				setFile(undefined)
				if (e?.status === 409) {
					onLogout(e?.status);
				}
				toast.error(e?.data?.msg || e.message);
			},
			complete: () => {
				setIsSubmitting(false);
			},
		});
	}, [file]);

	if (!patientID && type !== 'Anonymous') {
		return (
			<div className='flex h-screen items-center justify-center'>
				{/* <Loader /> */}
				<SimpleLoader />
			</div>
		);
	}

	const handleredirect = () => {
		// Don't show warning modal since recording continues in the background
		navigate(-1);
	};

	return (
		<>
			<div className='mb-24 px-6 pt-3'>
				<div className='mb-6 flex items-center justify-between'>
					<h3 className='mb-0 mt-4 text-2xl font-semibold'>Patient Information:</h3>
					<Button
						variant='outline'
						color='gray'
						colorIntensity='500'
						className='flex items-center gap-2 text-sm'
						onClick={() => handleredirect()}>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							className='h-4 w-4'
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M10 19l-7-7m0 0l7-7m-7 7h18'
							/>
						</svg>
						Back
					</Button>
				</div>

				{patientID && (
					<div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-slate-800'>
						<div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
							<div className='flex flex-col'>
								<span className='text-sm text-gray-500 dark:text-gray-400'>
									Patient Name
								</span>
								<span className='text-lg font-medium text-gray-900 dark:text-white'>
									{patientID.firstName} {patientID.lastName}
								</span>
							</div>
							<div className='flex flex-col'>
								<span className='text-sm text-gray-500 dark:text-gray-400'>
									Email
								</span>
								<span className='text-lg font-medium text-gray-900 dark:text-white'>
									{patientID.email}
								</span>
							</div>
							<div className='flex flex-col'>
								<span className='text-sm text-gray-500 dark:text-gray-400'>
									Mobile Number
								</span>
								<span className='text-lg font-medium text-gray-900 dark:text-white'>
									{patientID.moblieNo}
								</span>
							</div>
						</div>
					</div>
				)}

				<div className='my-2 border-b-2 py-3'>
					{caseInfo && (
						<div className='flex flex-wrap items-center gap-2'>
							<h3 className='text-2xl font-semibold'>Cases:</h3>
							<div className='flex flex-wrap items-center gap-2'>
								<p className='mb-0 text-xl'> {caseInfo?.caseName || ''} </p>
								<span className='text-md mt-1 text-gray-500'>
									{' '}
									Created At: {dateFormat(caseInfo?.createdAt) || ''}
								</span>
							</div>
						</div>
					)}
				</div>

				<div className='mx-auto mb-16 mt-12 flex flex-col items-center justify-center gap-5 md:mt-28'>
					<div className="relative inline-flex items-center bg-gray-100 rounded-full p-1 shadow-inner">
						<button
							type="button"
							className={`flex items-center justify-center py-2 px-5 rounded-full text-lg font-medium transition-all duration-200 
								${selectedNote === 'INITIAL_NOTE' 
									? 'primary-bg text-white shadow-md' 
									: 'text-gray-500 hover:text-gray-800'
								}`}
							onClick={() => setSelectedNote('INITIAL_NOTE')}
						>
							Initial Note
						</button>
						<button
							type="button"
							className={`flex items-center justify-center py-2 px-5 rounded-full text-lg font-medium transition-all duration-200 
								${selectedNote === 'FOLLOWUP_NOTE' 
									? 'primary-bg text-white shadow-md' 
									: 'text-gray-500 hover:text-gray-800'
								} ${!caseInfo ? 'opacity-50 cursor-not-allowed' : ''}`}
							onClick={() => caseInfo && setSelectedNote('FOLLOWUP_NOTE')}
							disabled={!caseInfo}
						>
							Follow-up Note
						</button>
					</div>
				</div>

				<AudioRecorderComponent selectedNote={selectedNote} key={patientID?.id || type} />
				<div className='flex items-center justify-center gap-10'>
					<hr className='w-16 border-black' />
					<h3 className='text-2xl font-bold'>OR</h3>
					<hr className='w-16 border-black' />
				</div>
				<FileUploadAudio file={file} setFile={setFile} isSubmitting={isSubmitting}  />
			</div>

			{/* Disable the modal completely since recordings continue in the background */}
			<Modal isOpen={false} setIsOpen={setIsModalOpen} isCentered size='sm'>
				<ModalHeader setIsOpen={setIsModalOpen}>Do you want to continue?</ModalHeader>

				<ModalBody>
					<p className='mb-0 text-lg'>
						Unsaved recordings will be lost if you switch patients{' '}
					</p>
				</ModalBody>
				<ModalFooter className={`'justify-center' 'pt-3 justify-end gap-3`}>
					<Button
						variant='solid'
						color='gray'
						colorIntensity='500'
						className='cursor-pointer px-4 py-2 text-white'
						onClick={() => {
							// Simply navigate away without stopping recording
							navigate(-1);
						}}>
						Leave
					</Button>
					<Button
						className='cursor-pointer px-4 py-2 text-white'
						color='green'
						variant='solid'
						colorIntensity='600'
						onClick={() => {
							setIsOnNewNoteScreen(false);
							setIsModalOpen(false);
						}}>
						Stay Here{' '}
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default NewNoteCase;